import React, { useState } from "react";
import { Loader2, Mail, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { subscribeToArchive } from "../../lib/api";

/**
 * SubscriberForm — in-universe "classified intel drop" capture card.
 *
 * Variants:
 *   - "hero"     : full-width below the homepage hero
 *   - "archive"  : compact band at the bottom of /archive
 *   - "dossier"  : footer card on /topic/<slug>
 *
 * Looks intentionally like a redacted Archive asset (aged paper, mono-red
 * label, hairline border, ink-stamp confirmation). Not a SaaS popup.
 */
const COPY = {
  hero: {
    label: "// CLASSIFIED — INTEL DROP",
    headline: "Receive declassified dispatches",
    body: "When the Archive publishes a new dossier or surfaces evidence the headlines won't touch, a one-line bulletin is filed to your inbox. No marketing. Unsubscribe with one click.",
    cta: "REQUEST ACCESS",
    ok: "Contact on file. The next dispatch will reach you when it's filed.",
  },
  archive: {
    label: "// FIELD NOTICE",
    headline: "Get notified when new dossiers are filed",
    body: "Quiet bulletins only. We don't pad the inbox. One-click unsubscribe.",
    cta: "FILE YOUR CONTACT",
    ok: "On file. Watch the inbox.",
  },
  dossier: {
    label: "// FOLLOW THIS CASE",
    headline: "Read more dossiers like this",
    body: "Get a quiet one-line bulletin when new investigative files are published. Bound to this dossier — useful for tracking follow-ups.",
    cta: "ADD ME TO THE LIST",
    ok: "Contact filed. Future updates on this case will reach you.",
  },
};

const isLikelyEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

export const SubscriberForm = ({
  variant = "archive",
  topicSlug = null,
  className = "",
}) => {
  const copy = COPY[variant] || COPY.archive;
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const testidPrefix = `subscriber-form-${variant}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const trimmed = email.trim();
    if (!isLikelyEmail(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const ref_path = typeof window !== "undefined" ? window.location.pathname : null;
      const res = await subscribeToArchive({
        email: trimmed,
        source: variant === "hero" ? "hero" : variant,
        topic_slug: variant === "dossier" ? topicSlug : null,
        ref_path,
        hp,
      });
      // Persist the subscriber tracking id so the LiveAudienceTracker can
      // bind this browser session to a known subscriber email (admin-only
      // visibility). No-op on duplicate signups that return no tracking_id.
      if (res?.tracking_id) {
        try { localStorage.setItem("archive_subscriber_token", res.tracking_id); } catch { /* noop */ }
      }
      setDone(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not file your contact. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Common surface: redacted aged-paper card
  const containerCls = [
    "relative border border-[#2D2A26] bg-[#0F0F0F] aged-paper overflow-hidden",
    variant === "hero" ? "p-8 md:p-10" : variant === "archive" ? "p-6 md:p-8" : "p-6 md:p-7",
    className,
  ].join(" ");

  if (done) {
    return (
      <section
        data-testid={`${testidPrefix}-success`}
        className={containerCls}
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5DD4B3]/40 to-transparent" />
        <div className="flex items-center gap-2 mb-3 text-[#5DD4B3]">
          <Check size={14} />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
            // CONTACT FILED
          </span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-[#E4DFD3] mb-2">
          {done.status === "already_subscribed"
            ? "You're already on file"
            : done.status === "resubscribed"
              ? "Welcome back to the briefing list"
              : "Contact received"}
        </h3>
        <p className="text-[#A39E93] leading-relaxed max-w-2xl">{copy.ok}</p>
        {done.tracking_id && (
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065]">
            // TRACKING ID:{" "}
            <code className="text-[#A39E93] break-all">{done.tracking_id}</code>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={containerCls} data-testid={testidPrefix}>
      {/* Top hairline red bar — reads as a classified header strip */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D92D20]/60 to-transparent" />

      <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-end">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              {copy.label}
            </span>
            <span className="hidden md:inline font-mono text-[10px] text-[#757065]">
              · FILE NO. 2026 / SUBSCRIBER LEDGER
            </span>
          </div>
          <h3
            className={`font-display text-[#E4DFD3] leading-tight mb-3 ${
              variant === "hero" ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"
            }`}
          >
            {copy.headline}
          </h3>
          <p className="text-[#A39E93] leading-relaxed max-w-2xl text-sm md:text-base">
            {copy.body}
          </p>
          <div className="mt-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
            <Lock size={10} /> Email is encrypted at rest · We don&apos;t sell lists · One-click unsubscribe
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="w-full md:w-[360px] space-y-2"
          autoComplete="off"
          noValidate
        >
          <label className="block">
            <span className="sr-only">Email</span>
            <div className="flex items-stretch border border-[#2D2A26] bg-[#0a0a0a] focus-within:border-[#D92D20] transition-colors">
              <span className="flex items-center px-3 text-[#757065]">
                <Mail size={14} />
              </span>
              <input
                type="email"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.address@example.com"
                data-testid={`${testidPrefix}-email`}
                disabled={busy}
                className="flex-1 bg-transparent outline-none px-1 py-3 font-mono text-sm text-[#E4DFD3] placeholder:text-[#454038] disabled:opacity-50"
              />
            </div>
          </label>
          {/* Honeypot — hidden from humans, often filled by bots */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
            <label>
              Comments
              <input
                type="text"
                tabIndex={-1}
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid={`${testidPrefix}-submit`}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em]"
          >
            {busy ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Filing…
              </>
            ) : (
              copy.cta
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default SubscriberForm;
